import { getMachinesSearchTaggingOptions } from "@aura-frontend/omnibus-az-client";
import { MachinePublic } from "@aura-frontend/omnibus-az-client";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { cn, Skeleton, Tooltip } from "@win-reality/react-ui";
import { Link } from "react-router-dom";

import chooseFieldBg from "@/assets/choose-field-bg.webp";
import { ROUTES } from "@/lib/routes";
import { CONTACT_SUPPORT_TOOLTIP } from "@/utils/machineHealth";

type CoachMachineCardProps = {
  field: MachinePublic;
};

const CoachMachineCard = ({ field }: CoachMachineCardProps) => {
  const isInactive = field.status !== "active";

  const CardWrapper: React.ElementType = isInactive ? "div" : Link;
  const cardProps = isInactive
    ? {}
    : { to: `${ROUTES.LIVE_TRACKING}/${field.machineUuid}` };

  return (
    <CardWrapper
      {...cardProps}
      className={cn(
        "relative block rounded border border-border-input bg-white p-6 transition-shadow",
        isInactive
          ? "cursor-not-allowed opacity-60"
          : "cursor-pointer hover:bg-gray-50 hover:shadow-md",
      )}
    >
      <div className="flex flex-col gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-text-light">
            {field.name}
          </p>
          <p className="text-sm text-text-light">{field.region}</p>
        </div>

        {isInactive && (
          <Tooltip content={CONTACT_SUPPORT_TOOLTIP} variant="dark">
            <div className="flex shrink-0 items-center gap-1">
              <span className="text-sm italic text-accent-dark-3">
                (under maintenance)
              </span>
              <InformationCircleIcon
                aria-label="machine info"
                className="h-4 w-4 text-accent-dark-3"
              />
            </div>
          </Tooltip>
        )}
      </div>
    </CardWrapper>
  );
};

const CoachMode = () => {
  const {
    data: fields,
    isLoading: loadingFields,
    error,
  } = useQuery({
    ...getMachinesSearchTaggingOptions(),
    select: (result) => result?.machines ?? [],
  });

  return (
    <div
      className="relative h-full w-full bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${chooseFieldBg})` }}
    >
      <div className="absolute left-0 top-0 h-full w-full max-w-[21rem] overflow-y-auto py-6 pl-16 pr-4">
        <div className="flex flex-col gap-3">
          {loadingFields && (
            <>
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </>
          )}

          {error && (
            <p className="text-sm text-error">
              Error loading SmartParks. {error.message}
            </p>
          )}

          {!loadingFields && fields && fields.length === 0 && (
            <p className="text-sm text-accent-dark-3">
              No SmartParks available.
            </p>
          )}

          {!loadingFields &&
            fields &&
            fields.length > 0 &&
            fields.map((field) => (
              <CoachMachineCard key={field.machineUuid} field={field} />
            ))}
        </div>
      </div>
    </div>
  );
};

export default CoachMode;
